import { Permission, PERMISSION_CONFIG, PermissionConfig } from '../constants/permissions';
import UserRole from '../constants/user-role';
import { FeatureType, useFeatures } from '../hooks/features';

/**
 * Check if a user has a specific permission based on their role and available features
 */
export function hasPermission(
  userRole: string,
  permission: Permission,
  featuresStore = useFeatures.getState()
): boolean {
  const config = PERMISSION_CONFIG[permission];

  if (!config) {
    console.warn(`Permission config not found for: ${permission}`);
    return false;
  }

  const hasRole = checkRoleRequirement(userRole, config);
  const hasFeature = checkFeatureRequirement(config, featuresStore);

  return hasRole && hasFeature;
}

/**
 * Check if a user has any of the specified permissions
 */
export function hasAnyPermission(
  userRole: string,
  permissions: Permission[],
  featuresStore = useFeatures.getState()
): boolean {
  return permissions.some((permission) => hasPermission(userRole, permission, featuresStore));
}

/**
 * Check if a user has all of the specified permissions
 */
export function hasAllPermissions(
  userRole: string,
  permissions: Permission[],
  featuresStore = useFeatures.getState()
): boolean {
  return permissions.every((permission) => hasPermission(userRole, permission, featuresStore));
}

/**
 * Check if the user's role satisfies the permission requirements
 */
function checkRoleRequirement(userRole: string, config: PermissionConfig): boolean {
  if (!config.roles || config.roles.length === 0) {
    return true; // No role requirement
  }

  return config.roles.includes(userRole as UserRole);
}

/**
 * Check if the required features are enabled
 */
function checkFeatureRequirement(
  config: PermissionConfig,
  featuresStore: ReturnType<typeof useFeatures.getState>
): boolean {
  if (!config.features || config.features.length === 0) {
    return true; // No feature requirement
  }

  if (config.requireAll) {
    // All features must be enabled
    return config.features.every((feature) => featuresStore.hasFeature(feature));
  }

  // At least one feature must be enabled
  return config.features.some((feature) => featuresStore.hasFeature(feature));
}

/**
 * Check if a user has a specific role
 */
export function hasRole(userRole: string, allowedRoles: UserRole | UserRole[]): boolean {
  const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
  return roles.includes(userRole as UserRole);
}

/**
 * Check if a specific feature is enabled
 */
export function hasFeature(feature: FeatureType, featuresStore = useFeatures.getState()): boolean {
  return featuresStore.hasFeature(feature);
}

/**
 * Get a user-friendly error message for permission denial
 */
export function getPermissionDeniedMessage(permission: Permission): string {
  const config = PERMISSION_CONFIG[permission];

  if (!config) {
    return 'Access denied: Unknown permission';
  }

  if (config.roles && config.roles.length > 0) {
    const roleNames = config.roles.join(', ');
    return `Access denied: Requires role(s): ${roleNames}`;
  }

  if (config.features && config.features.length > 0) {
    const featureNames = config.features.join(', ');
    return `Access denied: Requires feature(s): ${featureNames}`;
  }

  return 'Access denied';
}
